import React, { useState, useEffect } from "react";
import { Form, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { isEmpty, orderBy } from "lodash";
import { getFileName } from "src/utils/common";
import useGetTestPackageList from "src/components/TestPackage/useGetTestPackageList";
import EditableSelectField from "../EditableSelectField";
import useDeployInstance from "src/components/ProjectInstance/useDeployInstance";

const FORM_NAME = {
  INSTANCE_ID: "id",
  INCREMENT_ID: "increment_id",
  NAME: "name",
  TEST_PACKAGE_ID: "test_package_id",
  YAMCS_PACKAGE_ID: "yamcs_package_id",
  DASHBOARD_OPI: "opi_s3_key",
  MISSION_DATABASE: "mdb_s3_key",
  MDB_UPDATE: "mdb_update_at",
  OPI_UPDATE: "opi_update_at",
  MDB_HASH: "mdb_hash",
  OPI_HASH: "opi_hash",
};

const ModalDeployInstance = ({ instanceData, projectInstanceListRefetch, isOpen, toggle, setIsLoadingProjectInstance }) => {
  const { t } = useTranslation("project-instance", "common");
  const [isSubmit, setIsSubmit] = useState(false);
  const [isTestPackageEdit, setIsTestPackageEdit] = useState(false);
  const [testPackages, setTestPackages] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { testPackageList, isFetchingTestPackageList } = useGetTestPackageList({
    yamcs_package_id: instanceData?.id,
    isSelectOptions: true,
    refetchOnWindowFocus: false,
  });

  const { submitData } = useDeployInstance(toggle);

  const { control, setValue, getValues, handleSubmit } = useForm({
    defaultValues: {
      [FORM_NAME.TEST_PACKAGE_ID]: instanceData?.[FORM_NAME.TEST_PACKAGE_ID] || "",
    },
  });

  const handleDeployInstance = (data) => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    const deployData = {
      [FORM_NAME.YAMCS_PACKAGE_ID]: instanceData?.[FORM_NAME.INSTANCE_ID] || "",
      [FORM_NAME.TEST_PACKAGE_ID]: data?.[FORM_NAME.TEST_PACKAGE_ID] || "",
      [FORM_NAME.MISSION_DATABASE]: instanceData?.[FORM_NAME.MISSION_DATABASE] || "",
      [FORM_NAME.DASHBOARD_OPI]: instanceData?.[FORM_NAME.DASHBOARD_OPI] || "",
    };

    setIsSubmit(true);
    setIsLoadingProjectInstance(true);

    submitData({ data: deployData }, () => {
      setIsSubmit(false);
      setShowConfirmation(false);
      projectInstanceListRefetch();
    });
  };

  const handleCancel = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      toggle();
      document.body.click();
    }
  };

  const handleChangeTestPackage = (selectedOption) => {
    setValue(FORM_NAME.TEST_PACKAGE_ID, selectedOption);
  };

  const getTestPackageName = (testPackageId) => {
    const selectedPackage = testPackages.find((item) => item.value === testPackageId);
    return selectedPackage ? selectedPackage.label : "";
  };

  const getNewestCreatedTestPackage = (testPackages) => {
    if (!testPackages || testPackages.length === 0) return null;

    const packagesWithDates = testPackages.filter((item) => item.createdAt);

    if (packagesWithDates.length === 0) {
      return testPackages[0];
    }

    return orderBy(packagesWithDates, ["createdAt"], ["desc"])[0];
  };

  useEffect(() => {
    if (testPackageList) {
      setTestPackages(testPackageList || []);

      const newestTestPackage = getNewestCreatedTestPackage(testPackageList);
      setValue(FORM_NAME.TEST_PACKAGE_ID, newestTestPackage?.value);
    }
  }, [testPackageList]);

  useEffect(() => {
    if (!isOpen) {
      setShowConfirmation(false);
    }
  }, [isOpen]);

  const getInstanceIdAndName = (instanceID, instanceName) => {
    if (instanceID && instanceName) {
      return `#${instanceID}-${instanceName}`;
    }
    return instanceID || instanceName || "";
  };

  return (
    <Modal centered isOpen={isOpen} toggle={toggle} size="lg" backdrop="static" className="deploy-instance-modal">
      <ModalHeader>{showConfirmation ? t("deploy_modal.title_confirm") : t("deploy_modal.title")}</ModalHeader>
      <ModalBody className="p-b-0">
        {showConfirmation ? (
          <div className="mb-2">
            <p className="text-break-space text-center text-danger fw-bold">
              {t("deploy_modal.description_confirm", {
                instance_name: getInstanceIdAndName(instanceData?.[FORM_NAME.INCREMENT_ID], instanceData?.name),
              })}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-2">
              <strong>{t("deploy_modal.description")}:</strong>
            </div>
            <Form className="p-x-16 p-y-4 deploy-instance-form mb-3">
              <div className="row">
                <div className="col-md-12">
                  <div className="row mb-1 align-items-start">
                    <div className="col-md-4 p-y-8">
                      <label className="form-label mb-0">{t(`deploy_modal.${FORM_NAME.INSTANCE_ID}`)}:</label>
                    </div>
                    <div className="col-md-8 p-y-8">
                      {instanceData?.[FORM_NAME.INCREMENT_ID] && (
                        <p className="mb-0 text-break">#{instanceData?.[FORM_NAME.INCREMENT_ID]}</p>
                      )}
                    </div>
                  </div>

                  <div className="row mb-1 align-items-start">
                    <div className="col-md-4 p-y-8">
                      <label className="form-label mb-0">{t(`deploy_modal.${FORM_NAME.NAME}`)}:</label>
                    </div>
                    <div className="col-md-8 p-y-8">
                      <p className="mb-0 text-break">{instanceData?.[FORM_NAME.NAME] || ""}</p>
                    </div>
                  </div>

                  <div className="row mb-1 align-items-center">
                    <div className="col-md-4 p-y-8">
                      <label className="form-label mb-0">{t(`deploy_modal.${FORM_NAME.TEST_PACKAGE_ID}`)}:</label>
                    </div>
                    <div className="col-md-8 p-y-8">
                      <EditableSelectField
                        fieldName={FORM_NAME.TEST_PACKAGE_ID}
                        disabled={isSubmit}
                        value={getValues(FORM_NAME.TEST_PACKAGE_ID)}
                        control={control}
                        options={testPackages}
                        placeholder="Select test package"
                        onChangeExtra={handleChangeTestPackage}
                        getDisplayText={getTestPackageName}
                        isEditing={isTestPackageEdit}
                        onToggleEdit={() => setIsTestPackageEdit(!isTestPackageEdit)}
                      />
                    </div>
                  </div>

                  <div className="row mb-1 align-items-start">
                    <div className="col-md-4 p-y-8">
                      <label className="form-label mb-0">{t(`deploy_modal.${[FORM_NAME.DASHBOARD_OPI]}`)}:</label>
                    </div>
                    <div className="col-md-8 p-y-8">
                      <p className="mb-0 text-break">{`${
                        getFileName(
                          Array.isArray(instanceData?.[FORM_NAME.DASHBOARD_OPI])
                            ? instanceData?.[FORM_NAME.DASHBOARD_OPI]?.[0]
                            : instanceData?.[FORM_NAME.DASHBOARD_OPI]
                        ) || ""
                      } (${instanceData?.[FORM_NAME.OPI_UPDATE] || ""})`}</p>
                    </div>
                  </div>

                  <div className="row mb-1 align-items-start">
                    <div className="col-md-4 p-y-8">
                      <label className="form-label mb-0">{t(`deploy_modal.${FORM_NAME.MISSION_DATABASE}`)}:</label>
                    </div>
                    <div className="col-md-8 p-y-8">
                      <p className="mb-0 text-break">{`${
                        getFileName(
                          Array.isArray(instanceData?.[FORM_NAME.MISSION_DATABASE])
                            ? instanceData?.[FORM_NAME.MISSION_DATABASE]?.[0]
                            : instanceData?.[FORM_NAME.MISSION_DATABASE]
                        ) || ""
                      } (${instanceData?.[FORM_NAME.MDB_UPDATE] || ""})`}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </>
        )}
      </ModalBody>
      <ModalFooter className="justify-content-center p-a-16">
        <button className="btn btn-sm btn-secondary m-x-10 width-by-px-120" disabled={isSubmit} onClick={handleCancel}>
          {t("cancel")}
        </button>
        <button
          className="btn btn-sm btn-primary m-x-10 width-by-px-120"
          disabled={isSubmit || isFetchingTestPackageList || !instanceData?.is_deploy_allowed || isEmpty(testPackages)}
          onClick={handleSubmit((data) => handleDeployInstance(data))}
        >
          {isSubmit ? <Spinner size="sm" /> : t("deploy_modal.deploy")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalDeployInstance;
